<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getOutreachIndex, processOutreachFile, saveOutreachEntry,
    updateOutreachEntry, deleteOutreachEntry,
    type OutreachEntry,
  } from '../lib/api';
  import { renderSafeMarkdown } from '../lib/markdown';

  // Rendered as the Collateral tab of Teach George. The parent owns the page
  // title and reports the row count into the tab label.
  let { onCount }: { onCount?: (n: number) => void } = $props();

  let entries = $state<OutreachEntry[]>([]);
  let expandedId = $state('');
  let search = $state('');
  let showUpload = $state(false);

  // Upload state
  let whenToAttach = $state('');
  let file: File | null = $state(null);
  let processing = $state(false);
  let draft = $state<{ title: string; summary: string; when_to_attach: string; filename: string } | null>(null);
  let editTitle = $state('');
  let editWta = $state('');
  let editSummary = $state('');
  let toast = $state('');
  let saving = $state(false);

  // Browse-edit state
  let editingId = $state('');
  let browseWta = $state('');
  let browseSummary = $state('');
  let browseSaving = $state(false);

  // We need to keep the file reference for the save step
  let uploadedFile: File | null = $state(null);

  const filtered = $derived(
    search.trim()
      ? entries.filter(e => `${e.id} ${e.title} ${e.when_to_attach} ${e.original_filename}`.toLowerCase().includes(search.trim().toLowerCase()))
      : entries
  );

  // Two entries pointing at the same uploaded filename means the same document
  // was uploaded twice — surfacing it is how it gets cleaned up. Detected on
  // original_filename only; there is no content hash on the record.
  const duplicates = $derived.by(() => {
    const byName = new Map<string, string[]>();
    for (const e of entries) {
      const name = (e.original_filename || '').trim().toLowerCase();
      if (!name) continue;
      byName.set(name, [...(byName.get(name) || []), e.id]);
    }
    const out: Record<string, string[]> = {};
    for (const [, ids] of byName) {
      if (ids.length < 2) continue;
      for (const id of ids) out[id] = ids.filter(other => other !== id);
    }
    return out;
  });

  onMount(async () => {
    await refreshIndex();
  });

  async function refreshIndex() {
    entries = await getOutreachIndex();
    onCount?.(entries.length);
  }

  async function handleProcess() {
    if (!file) return;
    processing = true;
    try {
      const r = await processOutreachFile(file, whenToAttach);
      draft = r;
      editTitle = r.title;
      editWta = r.when_to_attach;
      editSummary = r.summary;
      uploadedFile = file;
    } catch (err: any) {
      showToast('Processing failed: ' + (err.message || ''), true);
    }
    processing = false;
  }

  async function handleSave() {
    if (!draft || !uploadedFile) return;
    saving = true;
    try {
      const r = await saveOutreachEntry(uploadedFile, {
        title: editTitle,
        summary: editSummary,
        when_to_attach: editWta,
      });
      showToast(`Saved ${r.entry_id} — ${r.title}`);
      draft = null;
      file = null;
      uploadedFile = null;
      editTitle = '';
      editWta = '';
      editSummary = '';
      whenToAttach = '';
      const el = document.querySelector('#mk-file-input') as HTMLInputElement;
      if (el) el.value = '';
      showUpload = false;
      await refreshIndex();
    } catch (err: any) {
      showToast('Save failed: ' + (err.message || ''), true);
    }
    saving = false;
  }

  // Had no catch: a failed delete re-rendered the row unchanged and said
  // nothing, so Remove just appeared not to respond. The save paths in this
  // file already report failures — this was the gap.
  async function handleDelete(entry: OutreachEntry) {
    if (!confirm(`Remove ${entry.id} — ${entry.title}?`)) return;
    try {
      await deleteOutreachEntry(entry.id);
      expandedId = '';
      await refreshIndex();
    } catch (err: any) {
      showToast('Could not remove ' + entry.id + ': ' + (err.message || ''), true);
    }
  }

  function startEdit(entry: OutreachEntry) {
    expandedId = entry.id;
    editingId = entry.id;
    browseWta = entry.when_to_attach;
    // Extract the summary portion from the detail markdown (after the --- separator)
    const parts = entry.detail.split('\n---\n');
    browseSummary = parts.length >= 2 ? parts[parts.length - 1].trim() : entry.detail;
  }

  function cancelEdit() {
    editingId = '';
  }

  async function handleUpdate(entry: OutreachEntry) {
    browseSaving = true;
    try {
      await updateOutreachEntry(entry.id, {
        when_to_attach: browseWta,
        summary: browseSummary,
      });
      showToast('Updated ' + entry.id);
      editingId = '';
      await refreshIndex();
    } catch (err: any) {
      showToast('Update failed: ' + (err.message || ''), true);
    }
    browseSaving = false;
  }

  function toggleExpand(id: string) {
    expandedId = expandedId === id ? '' : id;
    if (expandedId !== id) editingId = '';
  }

  // Failures must render via `.toast-error`, never the success box: DESIGN-SYSTEM
  // §1 reserves green for on-track status, and a failure told in green is worse
  // than no toast at all — it reads as confirmation.
  let toastError = $state(false);
  function showToast(msg: string, isError = false) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 3000);
  }
</script>

<div class="panehead">
  Files George can attach to an outreach email. He sends these whole — he does not quote from them.
</div>

<div class="toolbar">
  <div class="searchwrap">
    <svg class="searchicon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/></svg>
    <input class="search" type="text" placeholder="Search..." bind:value={search} />
  </div>
  <span class="sp"></span>
  <button class="today-mini-btn" onclick={() => showUpload = !showUpload}>
    {showUpload ? 'Close' : 'Upload'}
  </button>
</div>

{#if showUpload}
  <div class="uploader">
    <div class="uphead">
      <span class="upt">Upload marketing collateral</span>
      <span class="upnote">The original file is kept, so it can be attached to emails.</span>
    </div>
    <div class="upbody">
      <div class="uf wide">
        <label for="mk-file-input">File</label>
        <input id="mk-file-input" type="file" accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.xls,.pptx,.jpg,.jpeg,.png,.webp"
          onchange={(e) => file = (e.target as HTMLInputElement).files?.[0] ?? null} />
        <div class="hint">One-pagers, brochures, case studies, pitch decks. PDF, Word, slides, spreadsheet or image.</div>
      </div>
      <div class="uf wide">
        <label for="oc-wta">When should it be attached? <span class="opt">optional — AI will suggest</span></label>
        <textarea id="oc-wta" bind:value={whenToAttach}
          placeholder="e.g. When introducing GAUGE to a gold mining prospect" rows="2"></textarea>
      </div>
    </div>
    <div class="upfoot">
      <button class="btn-primary" onclick={handleProcess} disabled={!file || processing}>
        {#if processing}<span class="spinner"></span> Processing...{:else}Process with AI{/if}
      </button>
      <button class="today-mini-btn" onclick={() => showUpload = false}>Cancel</button>
    </div>
  </div>

  {#if draft}
    <div class="card mt-4">
      <div class="font-bold mb-3">Review extracted info from {draft.filename}</div>
      <div class="mb-2">
        <label class="text-xs text-dim" for="oc-edit-title">Title</label>
        <input id="oc-edit-title" bind:value={editTitle} />
      </div>
      <div class="mb-2">
        <label class="text-xs text-dim" for="oc-edit-wta">When to attach</label>
        <input id="oc-edit-wta" bind:value={editWta} />
      </div>
      <div class="mb-2">
        <label class="text-xs text-dim" for="oc-edit-summary">Summary (markdown)</label>
        <textarea id="oc-edit-summary" bind:value={editSummary} rows="12"></textarea>
      </div>
      <button class="btn-primary" onclick={handleSave} disabled={saving}>
        {#if saving}<span class="spinner"></span> Saving...{:else}Save to Outreach Library{/if}
      </button>
    </div>
  {/if}
{/if}

{#if entries.length === 0}
  <div class="empty-state">
    <div class="empty-state-text">No collateral yet. Use Upload to add a document.</div>
  </div>
{:else if filtered.length === 0}
  <div class="empty-state">
    <div class="empty-state-text">No documents match "{search}".</div>
  </div>
{:else}
  {#each filtered as entry}
    <div class="dcard" class:open={expandedId === entry.id}>
      <!-- Toggle is a real <button> with only phrasing content; actions are
           SIBLINGS. Was role="button" wrapping <button>s, with <div>s inside. -->
      <div class="drow">
        <!-- Won't collapse while editing — same guard as Today's draft head.
             toggleExpand() clears editingId, so a stray row click would otherwise
             discard what you were typing. -->
        <button class="drow-toggle" aria-expanded={expandedId === entry.id}
          onclick={() => { if (editingId === entry.id) return; toggleExpand(entry.id); }}>
          <span class="did">{entry.id}</span>
          <span class="dbody">
            <span class="dtitle-row">
              <span class="dtitle">{entry.title}</span>
              <span class="chev" aria-hidden="true">&rsaquo;</span>
            </span>
            <span class="dmeta">
              {#if entry.original_filename}<span class="fn">{entry.original_filename}</span>{/if}
              {#if entry.date}<span class="sep">·</span><span>added {entry.date}</span>{/if}
              {#if duplicates[entry.id]}
                <span class="sep">·</span>
                <span class="status-pill status-pill-amber">Same file as {duplicates[entry.id].join(', ')}</span>
              {/if}
            </span>
          </span>
        </button>
        <div class="dacts">
          <button class="today-mini-btn" onclick={() => startEdit(entry)}>Edit</button>
          <button class="today-mini-btn danger" onclick={() => handleDelete(entry)}>Remove</button>
        </div>
      </div>

      {#if expandedId === entry.id}
        <div class="dexp">
          {#if editingId === entry.id}
            <div class="mb-2">
              <label class="text-xs text-dim" for="browse-wta-{entry.id}">When to attach</label>
              <input id="browse-wta-{entry.id}" bind:value={browseWta} />
            </div>
            <div class="mb-2">
              <label class="text-xs text-dim" for="browse-summary-{entry.id}">Summary (markdown)</label>
              <textarea id="browse-summary-{entry.id}" bind:value={browseSummary} rows="12"></textarea>
            </div>
            <div class="row" style="gap:8px;">
              <button class="btn-primary" onclick={() => handleUpdate(entry)} disabled={browseSaving}>
                {#if browseSaving}<span class="spinner"></span> Saving...{:else}Save Changes{/if}
              </button>
              <button class="today-mini-btn" onclick={() => cancelEdit()}>Cancel</button>
            </div>
          {:else}
            <div class="text-xs text-dim mb-2">
              <strong>When to attach:</strong> {entry.when_to_attach}
            </div>
            <div class="md-content text-sm mb-3">
              {@html renderSafeMarkdown(entry.detail)}
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/each}
{/if}

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

<style>
  /* Buttons are the app's CANONICAL classes (`.btn-primary` / `.today-mini-btn`
     in app.css) — the same vocabulary Today and ClientView use, so radius (5px),
     padding, hover and disabled states are shared rather than re-guessed here.
     This block is only Today's own height unifier (its `.draft-actions` rule):
     base `.btn-primary` is 12.5px/6px vs mini 12px/5px, so without it a row
     holding both sits at two heights. */
  /* FILLED secondary. DESIGN-SYSTEM §2's middle tier — Send (solid coral) ›
     Edit/Cancel (filled neutral) › ✦ tools & Dismiss (transparent). The base
     `.today-mini-btn` in app.css is transparent; Today adds this fill in a
     scoped rule of its own, so reading only the base rule makes the tier look
     transparent. None of the buttons on this page are defer/drop, so they all
     fill. Hover is left to the base rule (brighter text + --border-strong). */
  .today-mini-btn { background: var(--bg-hover); }
  /* No max-width — the one-line description was wrapping mid-sentence at 560px
     while the rows beside it run the full width, which read as a broken column. */
  .panehead { font-size: 12.5px; color: var(--text-dim); margin-bottom: 14px; }

  /* Native corner resizer ignores border-radius and crosses the rounded corner
     (Firefox outlines it too). Scrolling still works. Plain selector, NOT
     :global() — that would strip drag-resize app-wide. See TeachGeorge.svelte. */
  textarea { resize: none; }


  /* Upload panel — same banded-header shape as Settings' cards, so a form that
     drops into the page reads as part of the page. Replaces a flat `.card` of
     stacked full-width fields with a two-column grid. */
  .uploader {
    border: 1px solid var(--border);
    border-radius: 9px;
    background: var(--bg-surface);
    margin-bottom: 12px;
    overflow: hidden;
  }
  .uphead {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    padding: 9px 15px;
    background: var(--bg-hover);
    border-bottom: 1px solid var(--border);
  }
  .uphead .upt { font-size: 14px; font-weight: 600; }
  .uphead .upnote { font-size: 11.5px; color: var(--text-dim); }
  .upbody { padding: 13px 15px; display: grid; grid-template-columns: 1fr 1fr; gap: 13px 18px; }
  .uf.wide { grid-column: 1 / -1; }
  /* Same small-caps field label as the Teach George edit grid. */
  .uf label {
    display: block;
    margin-bottom: 5px;
    font-size: 10px;
    font-family: var(--font-mono);
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .uf .opt { text-transform: none; letter-spacing: 0; font-family: var(--font-sans); opacity: 0.8; }
  .uf .hint { font-size: 11px; color: var(--text-dim); margin-top: 5px; line-height: 1.45; }
  .upfoot {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 15px;
    border-top: 1px solid var(--border);
  }

  .toolbar { display: flex; align-items: center; gap: 8px; margin-bottom: 11px; }
  .toolbar .sp { flex: 1; }
  .searchwrap { position: relative; display: flex; align-items: center; flex: 1; max-width: 260px; }
  .searchicon {
    position: absolute;
    left: 9px;
    width: 13px;
    height: 13px;
    fill: none;
    stroke: var(--text-dim);
    stroke-width: 2;
    stroke-linecap: round;
    pointer-events: none;
  }
  /* ⚠️ `.toolbar input.search`, NOT a bare `.search`. app.css styles inputs via
     `input:not([type="checkbox"]):not([type="radio"])` — specificity (0,2,1) —
     which silently beat the old `.search` rule (0,2,0), so every value in it was
     dead and the box rendered at app.css's 13px / 8px 12px / radius 6px instead.
     `.page-toolbar-search` (app.css) hit the same trap and was fixed the same way.
     Values match `.page-toolbar-search`'s intent: recessed `--bg`, transparent
     border, radius 5px so it lines up with the buttons beside it. */
  .toolbar input.search {
    width: 100%;
    /* NOT `.page-toolbar-search`'s recessed `--bg` + transparent border: that
       assumes a toolbar nested inside a surface (Contacts). This toolbar sits
       directly on the page background, where `--bg` renders the field
       invisible. Matches the `.dcard` rows below it instead. */
    background: var(--bg-surface);
    border: 1px solid var(--border);
    color: var(--text);
    border-radius: 5px;
    padding: 5px 10px 5px 28px;
    font-size: 12px;
    /* 1.4 matches the buttons' line-height, so the search and the Upload button
       beside it render at the same 29px. An input's default `normal` (~1.2) left
       it 3px shorter. */
    line-height: 1.4;
    font-family: inherit;
  }
  .toolbar input.search::placeholder { color: var(--text-dim); font-size: 11.5px; }
  /* app.css gives focused inputs an accent border + glow; a toolbar search is
     ambient, so it takes the quiet treatment `.page-toolbar-search` uses. */
  .toolbar input.search:focus {
    outline: none;
    border-color: var(--border-strong);
    box-shadow: none;
  }

  /* document rows */
  .dcard {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    margin-bottom: 7px;
    box-shadow: var(--shadow-sm);
  }
  .dcard:hover { border-color: var(--border-strong); }
  /* Whole row toggles, like Today's cards. Today can put role="button" on its
     header because its actions sit in a separate row; ours are inline, so that
     would nest interactives again. Instead .drow-toggle is `flex: 1` — its real
     box spans everything left of the actions, so the dead space is genuinely
     part of the control. One item in the a11y tree, no nesting.
     ⚠️ Do NOT reach for an absolutely-positioned ::after overlay: elementFromPoint
     reports the originating <button>, but a real click on the out-of-flow
     pseudo-element never fires its handler. See TeachGeorge.svelte. */
  .drow { display: flex; align-items: center; gap: 12px; padding: 12px 15px; }
  /* Real button, styled back to plain text so the row reads unchanged. */
  .drow-toggle {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }
  .did {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    color: var(--text-dim);
    flex-shrink: 0;
    width: 40px;
  }
  .dbody { flex: 1; min-width: 0; display: block; }
  /* 600 to match Today's row title (`.draft-client`) and the persona row's
     `.nm` — all three name the item in their row, so they carry one weight.
     Was 500, which made an identical-looking card read lighter here. */
  .dtitle { display: block; font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dmeta {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
    margin-top: 3px;
    font-size: 11px;
    color: var(--text-dim);
  }
  .dmeta .fn { font-family: var(--font-mono); }
  .dmeta .sep { opacity: 0.45; }
  /* Hover-revealed, but keyboard focus must not leave them unreachable. */
  .dacts { display: flex; gap: 7px; flex-shrink: 0; opacity: 0; transition: opacity 0.12s; }
  .dcard:hover .dacts, .dacts:focus-within { opacity: 1; }
  /* Chevron sits in the title row beside the title (DESIGN-SYSTEM §6), not
     adrift at the far end of the row. Rotation matches Today's `.draft-chev`:
     collapsed points DOWN ("expand ↓"), open points UP — the DECISIONS
     settled rule that `→` means navigate and ↓/↑ mean expand/collapse. */
  .dtitle-row { display: flex; align-items: center; gap: 7px; min-width: 0; }
  .chev {
    flex-shrink: 0;
    color: var(--text-dim);
    font-size: 15px;
    line-height: 1;
    transition: transform 0.12s;
    transform: rotate(90deg);
  }
  .dcard.open .chev { transform: rotate(270deg); }
  .dexp { padding: 12px 15px; border-top: 1px solid var(--border); background: var(--bg); }

</style>
